import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
// import { useSelector } from 'react-redux'; // Removed useSelector, assuming it's unused
import { useGetFulfillmentGroupQuery, useCreateShippingLabelMutation, useCreateReturnShippingLabelMutation } from '../../../redux/apis/endpoints/deliveryApi';
import { useConfirmLabelCreationMutation } from '../../../redux/apis/endpoints/orderApi'; // Added import
import { toast } from 'react-hot-toast'; // Import for toast notifications
// import ParcelInfoInput from '../../../components/shipping/ParcelInfoInput'; // REMOVED
import { InformationCircleIcon, TruckIcon, HomeIcon, UserCircleIcon, MapPinIcon } from '@heroicons/react/24/outline'; // Import for message icon

// Extracted Components
import ShipmentSummary from '../../../components/shipping/ShipmentSummary';
import ShipmentItemsList from '../../../components/shipping/ShipmentItemsList';
import GenerateLabelAction from '../../../components/shipping/GenerateLabelAction';

// Common Components (Corrected paths)
import LoadingSpinner from '../../../components/common/LoadingSpinner'; // Corrected path for page spinner
import ErrorMessage from '../../../components/common/ErrorMessage'; // Path seems correct
import Button from '../../../components/ui/Button'; // Corrected path for Button

// Temporary BackButton (or use common)
const BackButton = ({ onClick }) => <Button onClick={onClick} variant="secondary">Back</Button>; 

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return '?';
  const initials = name.match(/\b\w/g) || [];
  return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
};

// Helper function to generate a color based on username (simple hash)
const generateColor = (username) => {
  if (!username) return 'bg-gray-400'; // Default color
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 
    'bg-teal-500', 'bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500'
  ];
  return colors[Math.abs(hash) % colors.length];
};

const ShippingStatusPage = () => {
  const { fulfillmentGroupId } = useParams();
  const history = useHistory();
  const location = useLocation();
  
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const isReturnMode = queryParams.get('returnMode') === 'true';
  const returnOrderItemId = queryParams.get('orderItemId');
  
  const { 
    data: fulfillmentGroupData,
    isLoading: isLoadingFG,
    error: fgError,
  } = useGetFulfillmentGroupQuery(fulfillmentGroupId, {
    skip: !fulfillmentGroupId,
    refetchOnMountOrArgChange: true, // Ensure fresh data when page loads
  });

  useEffect(() => {
    if (fulfillmentGroupData) {
      console.log('StartShippingPage - fulfillmentGroupData on load/refresh:', JSON.parse(JSON.stringify(fulfillmentGroupData)));
      if (fulfillmentGroupData.outboundDeliveries) {
        console.log('StartShippingPage - outboundDeliveries on load/refresh:', JSON.parse(JSON.stringify(fulfillmentGroupData.outboundDeliveries)));
      }
    }
  }, [fulfillmentGroupData]);

  const [createShippingLabel, { 
    isLoading: isCreatingLabel, 
    error: createLabelError, 
    data: newLabelData // Data from a NEWLY created label
  }] = useCreateShippingLabelMutation();

  const [confirmLabelCreated, { isLoading: isConfirmingLabelCreation }] = useConfirmLabelCreationMutation(); // Added hook

  const [createReturnShippingLabel, { 
    isLoading: isCreatingReturnShippingLabel, 
    error: createReturnShippingLabelError, 
    data: newReturnShippingLabelData // Data from a NEWLY created return shipping label
  }] = useCreateReturnShippingLabelMutation();

  // Calculate aggregated parcel info for display
  const displayParcelInfo = useMemo(() => {
    const currentOrderItems = fulfillmentGroupData?.orderItems;
    const currentShippingOriginAddress = fulfillmentGroupData?.shippingOriginAddress;

    if (!currentOrderItems || currentOrderItems.length === 0) {
      return { length: 'N/A', width: 'N/A', height: 'N/A', weight: 'N/A', units: { distance: '', mass: ''}, error: 'No items in fulfillment group.' };
    }

    const originCountry = currentShippingOriginAddress?.country;
    const defaultDistanceUnit = originCountry === 'US' ? 'in' : 'cm';
    const defaultMassUnit = originCountry === 'US' ? 'lb' : 'kg';

    let totalWeight = 0;
    let maxWidth = 0;
    let maxLength = 0;
    let sumHeight = 0;
    let itemsHaveSufficientInfo = true;
    let parcelError = null;

    for (const item of currentOrderItems) {
      const prodParcelInfo = item.product?.parcelInfo;
      if (!prodParcelInfo || !prodParcelInfo.length || !prodParcelInfo.width || !prodParcelInfo.height || !prodParcelInfo.weight || !prodParcelInfo.units?.distance || !prodParcelInfo.units?.mass) {
        itemsHaveSufficientInfo = false;
        parcelError = `Product ${item.product?.name || item.product?.id || 'Unknown'} is missing parcel information.`;
        break;
      }
      let itemLength = parseFloat(prodParcelInfo.length);
      let itemWidth = parseFloat(prodParcelInfo.width);
      let itemHeight = parseFloat(prodParcelInfo.height);
      let itemWeight = parseFloat(prodParcelInfo.weight);

      if (isNaN(itemLength) || isNaN(itemWidth) || isNaN(itemHeight) || isNaN(itemWeight) ||
          itemLength <= 0 || itemWidth <= 0 || itemHeight <= 0 || itemWeight <= 0) {
          itemsHaveSufficientInfo = false; 
          parcelError = `Product ${item.product?.name || item.product?.id || 'Unknown'} has invalid parcel dimensions/weight.`;
          break;
      }
      
      let currentItemDistanceUnit = prodParcelInfo.units.distance.toLowerCase();
      let currentItemMassUnit = prodParcelInfo.units.mass.toLowerCase();

      // Convert item's dimensions to defaultDistanceUnit for aggregation
      if (currentItemDistanceUnit !== defaultDistanceUnit) {
          if (currentItemDistanceUnit === 'in' && defaultDistanceUnit === 'cm') {
              itemLength *= 2.54; itemWidth *= 2.54; itemHeight *= 2.54;
          } else if (currentItemDistanceUnit === 'cm' && defaultDistanceUnit === 'in') {
              itemLength /= 2.54; itemWidth /= 2.54; itemHeight /= 2.54;
          } else {
              itemsHaveSufficientInfo = false; 
              parcelError = `Cannot convert distance unit ${currentItemDistanceUnit} to ${defaultDistanceUnit} for product ${item.product?.name || item.product?.id}.`;
              break;
          }
      }
      // Convert item's weight to defaultMassUnit for aggregation
      if (currentItemMassUnit !== defaultMassUnit) {
          if (currentItemMassUnit === 'lb' && defaultMassUnit === 'kg') itemWeight *= 0.453592;
          else if (currentItemMassUnit === 'kg' && defaultMassUnit === 'lb') itemWeight /= 0.453592;
          else if (currentItemMassUnit === 'oz' && defaultMassUnit === 'kg') itemWeight *= 0.0283495;
          else if (currentItemMassUnit === 'kg' && defaultMassUnit === 'oz') itemWeight /= 0.0283495;
          else if (currentItemMassUnit === 'g' && defaultMassUnit === 'kg') itemWeight /= 1000;
          else if (currentItemMassUnit === 'kg' && defaultMassUnit === 'g') itemWeight *= 1000;
          else {
              itemsHaveSufficientInfo = false; 
              parcelError = `Cannot convert mass unit ${currentItemMassUnit} to ${defaultMassUnit} for product ${item.product?.name || item.product?.id}.`;
              break;
          }
      }

      totalWeight += itemWeight * (item.quantity || 1);
      if (itemLength > maxLength) maxLength = itemLength;
      if (itemWidth > maxWidth) maxWidth = itemWidth;
      sumHeight += itemHeight * (item.quantity || 1);
    }

    if (!itemsHaveSufficientInfo) {
        return { length: 'Error', width: 'Error', height: 'Error', weight: 'Error', units: { distance: defaultDistanceUnit, mass: defaultMassUnit }, error: parcelError || 'Could not aggregate parcel info due to missing or invalid product data.' };
    }
    
    if (totalWeight <=0 || maxLength <=0 || maxWidth <=0 || sumHeight <=0) {
        return { length: 'Error', width: 'Error', height: 'Error', weight: 'Error', units: { distance: defaultDistanceUnit, mass: defaultMassUnit }, error: 'Aggregated dimensions/weight are invalid (zero or negative). Please check product data.' };
    }

    return {
      length: maxLength.toFixed(1),
      width: maxWidth.toFixed(1),
      height: sumHeight.toFixed(1),
      weight: totalWeight.toFixed(2),
      units: { distance: defaultDistanceUnit, mass: defaultMassUnit },
      error: null
    };
  }, [fulfillmentGroupData]);

  // Determine effectiveLabelData to pass to GenerateLabelAction
  const effectiveLabelData = useMemo(() => {
    if (isReturnMode) {
      // For return mode, check for return label data
      if (newReturnShippingLabelData && newReturnShippingLabelData.success) {
        return newReturnShippingLabelData;
      }
      
      // Check existing return deliveries if available
      if (fulfillmentGroupData?.inboundDeliveries?.length > 0) {
        const existingInboundDelivery = fulfillmentGroupData.inboundDeliveries.find(
          d => d.deliveryLegType === 'inbound_return' && d.shippingLabelUrl && 
               d.relatedOrderItem?.toString() === returnOrderItemId
        );
        if (existingInboundDelivery) {
          return {
            success: true,
            labelUrl: existingInboundDelivery.shippingLabelUrl,
            trackingNumber: existingInboundDelivery.tracking_number,
            carrier: existingInboundDelivery.carrier,
          };
        }
      }
      
      return null; // No return label found
    }
    
    // Original outbound shipping logic
    if (newLabelData && newLabelData.success) {
      return newLabelData; // Use data from the just-created label
    }
    // Check existing deliveries directly, regardless of FG status, if a label URL is present
    if (fulfillmentGroupData?.outboundDeliveries?.length > 0) {
      const existingOutboundDelivery = fulfillmentGroupData.outboundDeliveries.find(
        d => d.deliveryLegType === 'outbound_group' && d.shippingLabelUrl
      );
      if (existingOutboundDelivery) {
        return {
          success: true, // Indicate that we have label data
          labelUrl: existingOutboundDelivery.shippingLabelUrl,
          trackingNumber: existingOutboundDelivery.tracking_number, // Ensure correct field name from your Delivery model
          carrier: existingOutboundDelivery.carrier,
        };
      }
    }
    return null; // No new label, and no existing valid label found
  }, [newLabelData, newReturnShippingLabelData, fulfillmentGroupData, isReturnMode, returnOrderItemId]);

  const handleGenerateLabel = async () => {
    if (!fulfillmentGroupData) return;
    
    try {
      const labelCreationResponse = await createShippingLabel({
        fulfillmentGroupId,
        orderId: fulfillmentGroupData?.order?.id
      }).unwrap();

      if (labelCreationResponse && labelCreationResponse.success) {
        // Call confirmLabelCreated from orderApi to invalidate tags
        await confirmLabelCreated({ 
          orderId: fulfillmentGroupData?.order?.id, 
          fulfillmentGroupId 
        }).unwrap();
        // newLabelData will be set by the createShippingLabel mutation, triggering re-render
        // The effectiveLabelData memo will pick up newLabelData.
      } else {
        // Handle cases where labelCreationResponse exists but success is not true (if applicable)
        console.warn('Label creation response did not indicate success:', labelCreationResponse);
      }
    } catch (err) {
      console.error('Error during label generation or confirmation:', err);
      // Error is already handled by createLabelError state variable for display
    }
  };

  const handleGenerateReturnLabel = async () => {
    if (!fulfillmentGroupData || !returnOrderItemId) return;
    
    try {
      const returnLabelCreationResponse = await createReturnShippingLabel({
        fulfillmentGroupId,
        orderId: fulfillmentGroupData?.order?.id,
        orderItemId: returnOrderItemId
      }).unwrap();

      if (returnLabelCreationResponse && returnLabelCreationResponse.success) {
        // Call confirmLabelCreated from orderApi to invalidate tags
        await confirmLabelCreated({ 
          orderId: fulfillmentGroupData?.order?.id, 
          fulfillmentGroupId,
          isReturn: true,
          orderItemId: returnOrderItemId
        }).unwrap();
        
        toast.success('Return shipping label created successfully!');
        // The effectiveReturnLabelData memo will pick up newReturnShippingLabelData.
      } else {
        console.warn('Return label creation response did not indicate success:', returnLabelCreationResponse);
      }
    } catch (err) {
      console.error('Error during return label generation or confirmation:', err);
      // Error is already handled by createReturnShippingLabelError state variable for display
    }
  };

  if (isLoadingFG) return <LoadingSpinner />; 
  if (fgError) return <ErrorMessage message={fgError.data?.message || fgError.error || 'Error loading fulfillment group details.'} />;
  if (!fulfillmentGroupData) return <div className="text-center p-4">Fulfillment group not found.</div>;

  const { order, shippingOriginAddress, orderItems, shippingRate } = fulfillmentGroupData;
  const customerShippingAddress = order?.shippingAddress;
  // const customer = order?.users_permissions_user; // Already populated in fulfillmentGroupData.order
  // const customerProfilePic = customer?.profilePic?.formats?.thumbnail?.url || customer?.profilePic?.url;
  // const customerName = customer?.username || 'Customer';
  // const customerInitials = getInitials(customerName);
  // const avatarColor = generateColor(customerName);

  const InfoRow = ({ icon, label, value, subValue, error }) => (
    <div className={`flex items-start py-2 ${error ? 'text-red-600' : 'text-gray-700'}`}>
      {React.cloneElement(icon, { className: "h-5 w-5 mr-3 mt-0.5 flex-shrink-0" })}
      <div className="flex-grow">
        <span className="font-medium">{label}:</span> {error ? <span className="font-semibold">{value}</span> : value}
        {subValue && <span className="block text-xs text-gray-500">{subValue}</span>}
      </div>
    </div>
  );
  
  const AddressDisplay = ({ title, address, icon }) => {
    if (!address) return <InfoRow icon={icon} label={title} value="Not available" error={true} />;
    const { name, street, apartment, city, state_province, postal_code, country_code, phone, email } = address;
    // Normalize address fields as they can vary (e.g. postalCode vs postal_code)
    const displayStreet = street || address.address_line1 || 'N/A';
    const displayApartment = apartment || address.address_line2 || '';
    const displayCity = city || address.city_locality || 'N/A';
    const displayState = state_province || address.state || '';
    const displayPostal = postal_code || address.postalCode || address.zip || 'N/A';
    const displayCountry = country_code || address.country || 'N/A';
    const displayPhone = phone || address.phoneNumber || '';
    const displayEmail = email || '';

    return (
        <div className="mb-3">
             <InfoRow 
                icon={icon} 
                label={title} 
                value={`${name ? name + ', ' : ''}${displayStreet}${displayApartment ? ', ' + displayApartment : ''}`} 
                subValue={`${displayCity}, ${displayState} ${displayPostal}, ${displayCountry}${displayPhone ? ' | Phone: '+displayPhone : ''}${displayEmail ? ' | Email: '+displayEmail : ''}`}
             />
        </div>
    );
};

  return (
    <div className="container mx-auto p-4 md:p-8">
      <div className="mb-6 flex justify-start">
        <BackButton onClick={() => history.goBack()} />
      </div>
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
        {isReturnMode 
          ? (effectiveLabelData ? 'Return Shipping Label Ready' : 'Generate Return Shipping Label')
          : (effectiveLabelData ? 'Shipping Label Ready' : 'Generate Shipping Label')
        }
      </h1>
      
      <div className="bg-white shadow-lg rounded-lg p-8 mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-4 border-b pb-2">
          {isReturnMode 
            ? `Return for Order Item in Fulfillment Group #${fulfillmentGroupId}`
            : `Fulfillment Group #${fulfillmentGroupId}`
          }
        </h2>
        
        <ShipmentSummary 
          customer={fulfillmentGroupData.customer} // Pass customer from order
          customerShippingAddress={customerShippingAddress}
          merchantOriginAddress={shippingOriginAddress}
          isReturnMode={isReturnMode}
        />

        {isReturnMode && returnOrderItemId ? (
          // For return mode, only show the specific item being returned
          <ShipmentItemsList 
            orderItems={orderItems.filter(item => item.id.toString() === returnOrderItemId)} 
            isReturnMode={true}
          />
        ) : (
          // For regular shipping, show all items
          <ShipmentItemsList orderItems={orderItems} />
        )}

        {/* Conditionally render ParcelInfoInput based on effectiveLabelData */}
        {!effectiveLabelData && (
            <div className="mb-6 border-t pt-6">
                {/* Titles and AddressDisplays removed as per user's diff/intent */}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2"> {/* Grid for InfoRows */}
                    <InfoRow 
                        icon={<TruckIcon />} 
                        label="Service Level" 
                        value={shippingRate?.servicelevel?.name || shippingRate?.servicelevel_name || shippingRate?.servicelevel?.token || 'N/A'} 
                        subValue={`Carrier: ${shippingRate?.provider || shippingRate?.carrier || 'N/A'} | Rate: ${shippingRate?.amount ? `${shippingRate.amount} ${shippingRate.currency}` : 'N/A'}`}
                    />
                    
                    {displayParcelInfo.error ? (
                        <div className="md:col-span-2"> {/* Error message spans 2 cols on medium+ screens */}
                            <InfoRow icon={<InformationCircleIcon/>} label="Package Info" value={displayParcelInfo.error} error={true}/>
                        </div>
                    ) : (
                        <> {/* Fragment for multiple items in one grid conceptual slot */}
                            <InfoRow 
                                icon={<MapPinIcon />} 
                                label="Dimensions" 
                                value={`${displayParcelInfo.length} x ${displayParcelInfo.width} x ${displayParcelInfo.height} ${displayParcelInfo.units.distance}`} 
                            />
                            <InfoRow 
                                icon={<MapPinIcon />}
                                label="Weight" 
                                value={`${displayParcelInfo.weight} ${displayParcelInfo.units.mass}`} 
                            />
                        </>
                    )}
                </div>

                {!displayParcelInfo.error && fulfillmentGroupData?.orderItems?.length > 1 && (
                    <div className="flex justify-start"> {/* Outer flex container */}
                        <div className="mt-2 flex items-start text-xs text-blue-700 bg-blue-50 p-1.5 rounded-md"> {/* Inner message div, removed w-fit */}
                            <InformationCircleIcon className="h-4 w-4 mr-1.5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span>This is the estimated combined package information that will be used to generate the shipping label. It's derived from the products in this fulfillment group.</span>
                        </div>
                    </div>
                )}
            </div>
        )}
        
        <GenerateLabelAction 
           onGenerate={isReturnMode ? handleGenerateReturnLabel : handleGenerateLabel}
           isLoading={isReturnMode ? isCreatingReturnShippingLabel || isConfirmingLabelCreation : isCreatingLabel || isConfirmingLabelCreation}
           isButtonDisabled={
                (isReturnMode 
                  ? (isCreatingReturnShippingLabel || isLoadingFG || !returnOrderItemId)
                  : (isCreatingLabel || isLoadingFG || !fulfillmentGroupData?.shippingOriginAddress)) ||
                !!effectiveLabelData || 
                isConfirmingLabelCreation ||
                !!displayParcelInfo.error
            }
           errorData={isReturnMode ? createReturnShippingLabelError : createLabelError}
           successData={effectiveLabelData}
           history={history}
           isReturnMode={isReturnMode}
        />
        
        {!effectiveLabelData && !fulfillmentGroupData?.shippingOriginAddress && // Safely access from fulfillmentGroupData
            <p className="text-xs text-red-500 mt-2">Cannot generate label: Merchant origin address is missing.</p>}

      </div>
    </div>
  );
};

export default ShippingStatusPage; 